import React, { useState } from "react";
import { CustomButton } from "@/components/ui/Buttons";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import CustomInputField from "@/components/form/CustomInputField";
import toast from "react-hot-toast";
import EyeIconBg from "@/assets/icons/settingsModal/EyeIconBg.svg";
import Eye from "@/assets/icons/Eye.svg";
import EyeCross from "@/assets/icons/EyeCross.svg";
import { useResetPasswordMutation } from "@/redux/feature/auth/authApi";
import Modal from "@/components/ui/Modal";

const ResetPasswordBanner = () => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="p-6 rounded-xl bg-amber-50 border border-amber-200 mb-4">
      <div className="flex flex-col md:flex-row gap-6 justify-between md:items-center">
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-500 mt-1 size-5" />
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2">
                <h2 className="text-base md:text-lg font-semibold text-gray-900">
                  Security Action Required
                </h2>
                <span className="inline-flex items-center gap-2 bg-amber-500 px-3 py-1 rounded-full">
                  <span className="size-2.5 rounded-full bg-white animate-pulse" />
                  <span className="text-white text-xs font-medium">
                    Password Change Required
                  </span>
                </span>
              </div>
              <p className="text-sm leading-6 text-gray-600">
                Your account is currently using a default password. For security
                reasons, please change your password to complete the account
                activation process. Accounts with default passwords may have
                limited access to features.
              </p>
            </div>
          </div>
        </div>
        <CustomButton
          onClick={() => setIsOpen(true)}
          variant="outline"
          color="#334155"
          fontSize="14px"
          fontWeight="600"
          border="1px solid #CBD5E1"
          borderRadius="100px"
          padding="12px 28px"
          className="leading-5 tracking-[-0.5px] hover:bg-amber-500 hover:text-white hover:border-amber-500 transition-colors"
        >
          Update Password
        </CustomButton>
      </div>
      <Modal width="480px" padding="24px" isOpen={isOpen} setOpen={setIsOpen}>
        <ChangePasswordModal setIsModalOpen={setIsOpen} />
      </Modal>
    </div>
  );
};

export default ResetPasswordBanner;

const ChangePasswordModal = ({
  setIsModalOpen,
}: {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  const [resetPassword] = useResetPasswordMutation();

  const handleOldPasswordChange = (value: string) => {
    setOldPassword(value);
  };

  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value);
    setPasswordsMatch(value === confirmPassword);
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    setPasswordsMatch(newPassword === value);
  };

  const handleSubmit = async () => {
    if (newPassword !== confirmPassword) {
      setPasswordsMatch(false);
      toast.error("New password and confirm password do not match.");
      return;
    }

    try {
      await resetPassword({
        oldPassword,
        password: confirmPassword,
      }).unwrap();
      toast.success("Password reset successfully");
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    }
  };

  return (
    <div className="overflow-y-auto">
      <div className="flex flex-col justify-start items-start">
        <EyeIconBg />
        <div className="space-y-2 mt-5 mb-6">
          <h6 className="text-[#101828] text-lg tracking-[-1.5%] leading-7 font-semibold">
            Change Password
          </h6>
          <p className="text-[#475467] text-sm leading-5">
            Enter your current password and a new password to change your
            password
          </p>
        </div>
        <div className="w-full mb-6">
          <div className="w-full space-y-6">
            <CustomInputField
              name="oldPassword"
              type="password"
              label="Enter old password"
              onChange={handleOldPasswordChange}
              value={oldPassword}
              placeholder="Enter your current password"
            />
          </div>
          <div className="space-y-6 mt-6">
            <CustomInputField
              name="newPassword"
              type="password"
              label="Enter new password"
              onChange={handleNewPasswordChange}
              value={newPassword}
              placeholder="Enter your new password"
            />
            <CustomInputField
              name="confirmPassword"
              type="password"
              label="Confirm new password"
              onChange={handleConfirmPasswordChange}
              value={confirmPassword}
              placeholder="Confirm your new password"
            />
            {!passwordsMatch && (
              <p className="text-red-500 text-sm">
                New password and confirm password do not match.
              </p>
            )}
          </div>
        </div>

        <div className="w-full flex gap-3 mt-5">
          <CustomButton
            onClick={() => setIsModalOpen(false)}
            variant="outline"
            fontWeight="600"
            padding="8px"
            fontSize="16px"
            color="#344054"
            border="1px solid #D0D5DD"
            backgroundColor="#FFFFFF"
            className="w-full leading-6"
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={handleSubmit}
            fontWeight="600"
            padding="8px"
            fontSize="16px"
            color="#FFFFFF"
            backgroundColor="#17457D"
            className="w-full"
          >
            Change Password
          </CustomButton>
        </div>
      </div>
    </div>
  );
};
